// Voice recording with the browser's native MediaRecorder.
//
// Replaces "mic-recorder-to-mp3": its lamejs dependency expects a global
// `Lame`, which does not exist in the production (Vite) build, so recording
// failed with "ReferenceError: Lame is not defined". The recorded audio is
// sent as is: the gateway (Evolution sendWhatsAppAudio, encoding=true)
// converts it to the voice-note format.

const FORMATS = [
  { mime: "audio/webm;codecs=opus", extension: "webm" },
  { mime: "audio/ogg;codecs=opus", extension: "ogg" },
  { mime: "audio/mp4", extension: "m4a" },
  { mime: "audio/webm", extension: "webm" },
];

export const pickRecordingFormat = isTypeSupported =>
  FORMATS.find(format => {
    try {
      return isTypeSupported(format.mime);
    } catch (err) {
      return false;
    }
  }) || null;

const extensionFromMime = mime => {
  const base = String(mime || "").split(";")[0];
  if (base.includes("ogg")) return "ogg";
  if (base.includes("mp4") || base.includes("aac")) return "m4a";
  return "webm";
};

export const createAudioRecorder = () => {
  let stream = null;
  let recorder = null;
  let chunks = [];
  let format = null;

  const release = () => {
    if (stream) stream.getTracks().forEach(track => track.stop());
    stream = null;
    recorder = null;
    chunks = [];
  };

  return {
    async start() {
      if (typeof MediaRecorder === "undefined") {
        throw new Error("MediaRecorder is not supported by this browser");
      }

      stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      try {
        format = pickRecordingFormat(mime => MediaRecorder.isTypeSupported(mime));
        recorder = format
          ? new MediaRecorder(stream, { mimeType: format.mime })
          : new MediaRecorder(stream);
        chunks = [];
        recorder.ondataavailable = event => {
          if (event.data && event.data.size) chunks.push(event.data);
        };
        recorder.start();
      } catch (err) {
        release();
        throw err;
      }
    },

    // Resolves to { blob, extension }, or null when nothing was recording.
    stop() {
      return new Promise(resolve => {
        if (!recorder || recorder.state === "inactive") {
          release();
          resolve(null);
          return;
        }

        const current = recorder;
        current.onstop = () => {
          const mime = current.mimeType || (format && format.mime) || "audio/webm";
          const blob = new Blob(chunks, { type: String(mime).split(";")[0] });
          const extension = format ? format.extension : extensionFromMime(mime);
          release();
          resolve({ blob, extension });
        };
        current.stop();
      });
    },
  };
};
